import { use, useEffect, useState } from 'react';
import { logger } from '~portal/lib/logger';
import { retryableImport, type RetryOptions } from '~portal/lib/retryable-lazy';

/**
 * Lazy fixture domains (UI round 2, Lane G). The operators and regions fixtures are about 60 kB of
 * minified source between them, and only the operators and regions pages read them — so they are
 * dynamic imports, fetched behind those routes, instead of riding in the entry chunk every chat
 * user and the standalone build download. The two ways in:
 *
 * - {@link useFixture} suspends until the domain has loaded and then returns it synchronously,
 *   which keeps the fixture hooks' `{ data, isLoading: false }` contract for the pages. The
 *   routes that render them preload the domain in their `loader` (`preloadListingFixtures`), so
 *   the chunk is fetched alongside the route's own and, by render, has usually arrived.
 * - {@link useFixtureModule} never suspends: it returns `undefined` until the domain arrives, for
 *   a live hook that can say "still loading" itself (the home page's tracked pages).
 *
 * A failed load is retried once (`retryableImport`); a second failure reaches the route's error
 * component, or the live hook's `error`, and is forgotten so that "Try again" requests it afresh.
 */

/** @desc A promise carrying React's thenable status fields, so `use` reads a settled one at once. */
type TrackedPromise<M> = Promise<M> & {
  status?: 'pending' | 'fulfilled' | 'rejected';
  value?: M;
  reason?: unknown;
};

export type LazyFixture<M> = {
  /** @desc Names the domain in log lines. */
  name: string;
  /** @desc Starts the import (once; again after a failure) and returns it. */
  load: () => Promise<M>;
  /** @desc The domain if it has already loaded, else `undefined`. Never starts a load. */
  peek: () => M | undefined;
};

export function lazyFixture<M>(
  name: string,
  importFn: () => Promise<M>,
  retry?: RetryOptions,
): LazyFixture<M> {
  let pending: TrackedPromise<M> | undefined;
  let loaded: M | undefined;

  const load = (): Promise<M> => {
    if (pending) return pending;
    const promise: TrackedPromise<M> = retryableImport(importFn, retry);
    // React's `use` honours these fields: a promise it finds fulfilled is read without suspending,
    // which is what lets a loader-preloaded domain render in the same pass.
    promise.status = 'pending';
    void promise.then(
      (value) => {
        promise.status = 'fulfilled';
        promise.value = value;
        loaded = value;
      },
      (reason: unknown) => {
        promise.status = 'rejected';
        promise.reason = reason;
        if (pending === promise) pending = undefined;
      },
    );
    pending = promise;
    return promise;
  };

  return { name, load, peek: () => loaded };
}

/** @desc Suspends until `fixture` has loaded; throws its load error to the nearest boundary. */
export function useFixture<M>(fixture: LazyFixture<M>): M {
  return use(fixture.load());
}

export type FixtureModule<M> = { module: M | undefined; error: Error | null };

/**
 * @desc The non-suspending read: `module` is `undefined` until `fixture` has loaded (or while
 * `enabled` is false, when nothing is requested), `error` is set if it could not be.
 */
export function useFixtureModule<M>(fixture: LazyFixture<M>, enabled = true): FixtureModule<M> {
  const [state, setState] = useState<FixtureModule<M>>(() => ({
    module: fixture.peek(),
    error: null,
  }));

  useEffect(() => {
    if (!enabled || state.module !== undefined) return undefined;
    let current = true;
    void fixture.load().then(
      (module) => {
        if (current) setState({ module, error: null });
      },
      (error: unknown) => {
        logger.error('[fixtures] Fixture domain failed to load', {
          fixture: fixture.name,
          error: error instanceof Error ? error.message : String(error),
        });
        if (current) {
          setState({
            module: undefined,
            error: error instanceof Error ? error : new Error(String(error)),
          });
        }
      },
    );
    return () => {
      current = false;
    };
  }, [fixture, enabled, state.module]);

  return state;
}

export const operatorFixtures = lazyFixture('operators', () => import('./operators'));
export const regionFixtures = lazyFixture('regions', () => import('./regions'));

/**
 * @desc For the `loader` of every route whose page reads operators or regions fixtures: both
 * domains, fetched in parallel with the route's component chunk. The operators fixture imports the
 * regions one, so asking for both costs no extra request.
 */
export const preloadListingFixtures = async (): Promise<void> => {
  await Promise.all([operatorFixtures.load(), regionFixtures.load()]);
};

/** @desc Every lazy domain at once — for the test setup, so fixture hooks render synchronously. */
export const preloadAllFixtures = async (): Promise<void> => {
  await Promise.all([operatorFixtures.load(), regionFixtures.load()]);
};
